import { useEffect, useState, type FormEvent } from 'react';
import {
  PHONEBOOK_NUMBER_KINDS, contactName,
  type Phonebook, type PhonebookAccess, type PhonebookContact, type PhonebookNumberKind,
} from '@arcnet/contracts';
import { ApiClientError } from '../../lib/api/client.js';
import {
  useCompanies, useContacts, useDeleteContact, usePhonebookAccess, useSaveContact,
} from '../../lib/api/hooks-phonebook.js';
import { Empty, ErrorBanner, SearchInput, SectionHead, Segmented, TableSkeleton, EditorHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { ContactUpload } from './ContactUpload.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { DialButton } from '../agent/CallBar.js';

/**
 * The account's phonebook (docs/32): contacts in the account's book, a department's, or the
 * user's own. Sent to the phones, and naming callers. The client's only: the API answers
 * nobody above the account.
 */

const PAGE = 200;

const KIND_LABEL: Readonly<Record<PhonebookNumberKind, string>> = {
  work: 'Work', mobile: 'Mobile', home: 'Home', other: 'Other',
};

const BOOK_HINT: Readonly<Record<Phonebook, string>> = {
  account: 'Everybody on the account sees these.',
  department: 'The department’s phones see these.',
  personal: 'Only you see these.',
};

/** Which book is open, and whose. */
interface Place { book: Phonebook; departmentId: string; extensionId: string }

export function ContactsPage({ tenantId }: { tenantId: string }) {
  const access = usePhonebookAccess(tenantId);
  const [place, setPlace] = useState<Place>({ book: 'account', departmentId: '', extensionId: '' });
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [editing, setEditing] = useState<PhonebookContact | 'new' | null>(null);
  const [uploading, setUploading] = useState(false);
  const a = access.data;

  // The first department and the first own extension, once they are known.
  useEffect(() => {
    if (!a) return;
    setPlace((p) => ({
      ...p,
      departmentId: p.departmentId || a.departments[0]?.id || '',
      extensionId: p.extensionId || a.personal[0]?.id || '',
    }));
  }, [a]);
  useEffect(() => { setLimit(PAGE); setEditing(null); }, [place.book, place.departmentId, place.extensionId, q]);
  useEffect(() => { setUploading(false); }, [place.book, place.departmentId, place.extensionId]);

  const ready = place.book === 'account'
    || (place.book === 'department' && place.departmentId !== '')
    || (place.book === 'personal' && place.extensionId !== '');
  const contacts = useContacts(tenantId, {
    book: place.book,
    department_id: place.book === 'department' ? place.departmentId : undefined,
    extension_id: place.book === 'personal' ? place.extensionId : undefined,
    q: q.trim() || undefined,
    limit,
  }, ready && !!a);
  const remove = useDeleteContact(tenantId);

  if (access.error) return <ErrorBanner error={access.error} />;

  const mayWrite = place.book === 'personal' || (a?.can_write ?? false);
  const rows = contacts.data?.data ?? [];
  const total = contacts.data?.total ?? 0;
  const books: Array<readonly [Phonebook, string]> = [
    ['account', 'Account'],
    ...(a && a.departments.length > 0 ? [['department', 'Departments'] as const] : []),
    ...(a && a.personal.length > 0 ? [['personal', 'My contacts'] as const] : []),
  ];

  // The editor takes the list's place, as on Queues (UI review, 3 October 2026).
  if (editing !== null && a) {
    const who = editing === 'new' ? 'New contact'
      : [editing.first_name, editing.last_name].filter(Boolean).join(' ') || 'Contact';
    return (
      <>
        <EditorHead back="Contacts" onBack={() => setEditing(null)} title={who} />
        <ContactEditor key={editing === 'new' ? 'new' : editing.id} tenantId={tenantId} access={a} place={place}
          contact={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
      </>
    );
  }

  return (
    <>
      <SectionHead title="Contacts" sub={BOOK_HINT[place.book]}
        action={mayWrite && ready && (
          <div className="arc-row" style={{ gap: 6 }}>
            <button type="button" className="arc-btn arc-btn-sm" aria-pressed={uploading}
              onClick={() => { setUploading((u) => !u); setEditing(null); }}>
              Upload
            </button>
            <button disabled={editing === 'new'} type="button" className="arc-btn arc-btn-primary arc-btn-sm"
              onClick={() => { setEditing(editing === 'new' ? null : 'new'); setUploading(false); }}>
              <Icon name="plus" size={13} />
              New contact
            </button>
          </div>
        )} />

      <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {books.length > 1 && (
          <Segmented label="Phonebook" value={place.book} options={books}
            onChange={(book) => setPlace((p) => ({ ...p, book }))} />
        )}
        {place.book === 'department' && a && (
          <select className="arc-select" style={{ maxWidth: 220 }} aria-label="Department"
            value={place.departmentId} onChange={(e) => setPlace((p) => ({ ...p, departmentId: e.target.value }))}>
            {a.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
        {place.book === 'personal' && a && a.personal.length > 1 && (
          <select className="arc-select" style={{ maxWidth: 220 }} aria-label="Extension"
            value={place.extensionId} onChange={(e) => setPlace((p) => ({ ...p, extensionId: e.target.value }))}>
            {a.personal.map((x) => <option key={x.id} value={x.id}>{`${x.number} ${x.name}`}</option>)}
          </select>
        )}
      </div>

      {uploading && a && (
        <ContactUpload tenantId={tenantId} book={place.book}
          departmentId={place.book === 'department' ? place.departmentId : null}
          extensionId={place.book === 'personal' ? place.extensionId : null}
          where={bookName(place, a)} onDone={() => setUploading(false)} />
      )}
      {(contacts.error || remove.error) && <ErrorBanner error={contacts.error ?? remove.error} />}

      <div className="arc-card">
        <div className="arc-card-head">
          <SearchInput value={q} onChange={setQ} placeholder="Search name, company, email or number"
            label="Search contacts" />
        </div>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr><th>Name</th><th>Company</th><th>Numbers</th><th>Email</th><th /></tr>
            </thead>
            {contacts.isLoading || !a ? <TableSkeleton cols={5} /> : (
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight: 550 }}>{contactName(c)}</div>
                      {c.job_title && <div className="arc-subtle" style={{ fontSize: 11.5 }}>{c.job_title}</div>}
                    </td>
                    <td>{c.first_name || c.last_name ? c.company_name ?? '' : ''}</td>
                    <td>
                      {c.numbers.map((n, i) => (
                        <div key={i}>
                          <span className="arc-mono">{n.number}</span>
                          <span className="arc-subtle" style={{ fontSize: 11.5 }}> {KIND_LABEL[n.kind].toLowerCase()}</span>
                          <DialButton number={n.number} />
                        </div>
                      ))}
                    </td>
                    <td>{c.email ?? ''}</td>
                    <td className="arc-td-right">
                      {mayWrite && (
                        <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                          <RowAction kind="edit" label={`Edit ${contactName(c)}`} onClick={() => setEditing(c)} />
                          <RowAction kind="delete" label={`Delete ${contactName(c)}`} disabled={remove.isPending}
                            onClick={() => { void confirmAction({ title: `Delete ${contactName(c)}?`, action: 'Delete', danger: true }).then((ok) => { if (ok) remove.mutate(c.id); }); }} />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {contacts.data !== undefined && a && rows.length === 0 && (
          <Empty icon="users" title={q ? 'No contacts match' : 'No contacts'}
            hint={q ? 'Try another name or number.' : 'Add the people you call, and your phones list them.'} />
        )}
        {rows.length < total && (
          <div className="arc-card-body arc-row" style={{ gap: 10 }}>
            <span className="arc-subtle">{`${rows.length} of ${total}`}</span>
            <button type="button" className="arc-btn arc-btn-sm" disabled={contacts.isFetching}
              onClick={() => setLimit((l) => Math.min(l + PAGE, 1000))}>
              Show more
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/** "the account's phonebook", "Sales' phonebook", "your contacts". */
function bookName(place: Pick<Place, 'book' | 'departmentId'>, access: PhonebookAccess): string {
  if (place.book === 'account') return 'the account’s phonebook';
  if (place.book === 'personal') return 'your contacts';
  const name = access.departments.find((d) => d.id === place.departmentId)?.name;
  return name ? `${name}’s phonebook` : 'the department’s phonebook';
}

interface NumberDraft { kind: PhonebookNumberKind; number: string }

function ContactEditor({ tenantId, access, place, contact, onDone }: {
  tenantId: string; access: PhonebookAccess; place: Place;
  contact: PhonebookContact | null; onDone: () => void;
}) {
  const save = useSaveContact(tenantId);
  const companies = useCompanies(tenantId);
  const [first, setFirst] = useState(contact?.first_name ?? '');
  const [last, setLast] = useState(contact?.last_name ?? '');
  const [title, setTitle] = useState(contact?.job_title ?? '');
  const [companyId, setCompanyId] = useState(contact?.company_id ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [notes, setNotes] = useState(contact?.notes ?? '');
  const [numbers, setNumbers] = useState<NumberDraft[]>(
    contact?.numbers.length ? contact.numbers : [{ kind: 'mobile', number: '' }]);
  const errors = save.error instanceof ApiClientError ? save.error.fieldErrors : {};

  // A contact stays in the book it is in; a new one goes in the book that is open.
  const book = contact?.book ?? place.book;
  const departmentId = contact ? contact.department_id : place.book === 'department' ? place.departmentId : null;
  const extensionId = contact ? contact.extension_id : place.book === 'personal' ? place.extensionId : null;
  const named = first.trim() !== '' || last.trim() !== '' || companyId !== '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({
      ...(contact ? { id: contact.id } : {}),
      book, department_id: departmentId, extension_id: extensionId,
      company_id: companyId || null,
      first_name: first, last_name: last, job_title: title, email, notes,
      numbers: numbers.filter((n) => n.number.trim() !== ''),
    }, { onSuccess: onDone });
  };
  const setNumber = (i: number, patch: Partial<NumberDraft>) =>
    setNumbers((ns) => ns.map((n, j) => (j === i ? { ...n, ...patch } : n)));
  const where = bookName({ book, departmentId: departmentId ?? '' }, access);

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={submit}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ct-first">First name</label>
          <input id="ct-first" className="arc-input" autoFocus maxLength={80}
            value={first} onChange={(e) => setFirst(e.target.value)} />
          {errors['first_name'] && <div className="arc-error">{errors['first_name']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ct-last">Last name</label>
          <input id="ct-last" className="arc-input" maxLength={80}
            value={last} onChange={(e) => setLast(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ct-company">Company</label>
          <select id="ct-company" className="arc-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">None</option>
            {(companies.data?.data ?? []).map((co) => <option key={co.id} value={co.id}>{co.name}</option>)}
          </select>
          {errors['company_id'] && <div className="arc-error">{errors['company_id']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ct-title">Job title</label>
          <input id="ct-title" className="arc-input" maxLength={80}
            value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
      </div>

      <div className="arc-field" style={{ margin: '12px 0 0' }}>
        <span className="arc-label">Numbers</span>
        {numbers.map((n, i) => (
          <div key={i} className="arc-row" style={{ gap: 6, marginBottom: 6 }}>
            <select className="arc-select" style={{ maxWidth: 120 }} aria-label={`Number ${i + 1} is`}
              value={n.kind} onChange={(e) => setNumber(i, { kind: e.target.value as PhonebookNumberKind })}>
              {PHONEBOOK_NUMBER_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
            </select>
            <input className="arc-input arc-mono" style={{ maxWidth: 220 }} inputMode="tel" maxLength={30}
              aria-label={`Number ${i + 1}`} placeholder="083 123 4567"
              value={n.number} onChange={(e) => setNumber(i, { number: e.target.value })} />
            <RowAction kind="delete" label={`Remove number ${i + 1}`}
              onClick={() => setNumbers((ns) => ns.filter((_, j) => j !== i))} />
            {errors[`numbers.${i}.number`] && <span className="arc-error">{errors[`numbers.${i}.number`]}</span>}
          </div>
        ))}
        {numbers.length < 8 && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
            onClick={() => setNumbers((ns) => [...ns, { kind: 'work', number: '' }])}>
            <Icon name="plus" size={13} /> Add number
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginTop: 12 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ct-email">Email</label>
          <input id="ct-email" className="arc-input" type="email" maxLength={254}
            value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ct-notes">Notes</label>
          <textarea id="ct-notes" className="arc-input" rows={2} maxLength={2000}
            value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      {save.error && Object.keys(errors).length === 0 && <ErrorBanner error={save.error} />}
      <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
        <span className="arc-subtle" style={{ fontSize: 11.5 }}>{`In ${where}.`}</span>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !named}>
          {save.isPending ? 'Saving…' : contact ? 'Save contact' : 'Add contact'}
        </button>
      </div>
    </form>
  );
}
